import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useContacts } from '../../hooks/useContacts';
import { 
  Search, Plus, Star, Mail, Phone, Building, Filter,
  FileText, ChevronRight, X, Trash2, Edit, Users as UsersIcon,
  TrendingUp, Send
} from 'lucide-react';
import AddContactModal from './AddContactModal';
import EditContactModal from './EditContactModal';

const MobileContacts = () => {
  const navigate = useNavigate();
  const {
    contacts,
    stats,
    loading,
    error,
    fetchContacts,
    createContact,
    updateContact,
    deleteContact,
    toggleFavorite
  } = useContacts();

  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  // Filter contacts
  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = searchQuery === '' || 
      contact.company_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.last_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      contact.email?.toLowerCase().includes(searchQuery.toLowerCase());

    const matchesFilter = 
      filterType === 'all' ||
      (filterType === 'favorites' && contact.is_favorite) ||
      (filterType === 'invoice_count' && contact.invoice_count > 0);

    return matchesSearch && matchesFilter;
  });

  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length > 2 || query.length === 0) {
      await fetchContacts({ search: query, filter: filterType });
    }
  };

  const handleAddContact = async (contactData) => {
    await createContact(contactData);
    setShowAddModal(false);
  };

  const handleEditContact = async (contactId, contactData) => {
    await updateContact(contactId, contactData);
    setEditingContact(null);
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white sticky top-0 z-10 shadow-sm">
        <div className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h1 className="text-xl font-bold text-gray-900">Contacts</h1>
            <button
              onClick={() => setShowAddModal(true)}
              className="p-2 bg-[#1F11ED] text-white rounded-lg hover:bg-[#1709C7]"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          {/* Search Bar */}
          <div className="flex items-center space-x-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search contacts..."
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
              />
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-2 rounded-lg border transition-colors ${
                showFilters || filterType !== 'all'
                  ? 'bg-[#1F11ED] text-white border-[#1F11ED]'
                  : 'bg-white text-gray-600 border-gray-300'
              }`}
            >
              <Filter className="w-5 h-5" />
            </button>
          </div>

          {/* Filters */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="space-y-3"
            >
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Filter By</label>
                <select
                  value={filterType}
                  onChange={(e) => {
                    setFilterType(e.target.value);
                    handleFilterChange(e.target.value);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F11ED]"
                >
                  <option value="all">All Contacts</option>
                  <option value="favorites">Favorites</option>
                  <option value="invoice_count">Has Invoices</option>
                </select>
              </div>
              <button
                onClick={() => {
                  setFilterType('all');
                  setSearchQuery('');
                  setShowFilters(false);
                }}
                className="w-full text-sm text-gray-600 py-2"
              >
                Clear Filters
              </button>
            </motion.div>
          )}
        </div>

        {/* Stats */}
        {stats && (
          <div className="px-4 pb-4">
            <div className="grid grid-cols-3 gap-3">
              <div className="bg-blue-50 p-3 rounded-lg text-center">
                <p className="text-2xl font-bold text-blue-600">{stats.total_contacts || 0}</p>
                <p className="text-xs text-blue-700">Total</p>
              </div>
              <div className="bg-purple-50 p-3 rounded-lg text-center">
                <p className="text-2xl font-bold text-purple-600">{stats.favorite_contacts || 0}</p>
                <p className="text-xs text-purple-700">Favorites</p>
              </div>
              <div className="bg-green-50 p-3 rounded-lg text-center">
                <p className="text-2xl font-bold text-green-600">{stats.contacts_with_invoices || 0}</p>
                <p className="text-xs text-green-700">Active</p>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Contact List */}
      <div className="p-4 space-y-3">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="h-24 bg-gray-200 rounded-xl animate-pulse"></div>
            ))}
          </div>
        ) : filteredContacts.length === 0 ? (
          <div className="text-center py-12">
            <UsersIcon className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No contacts found</h3>
            <p className="text-sm text-gray-600 mb-4">
              {searchQuery || filterType !== 'all' 
                ? 'Try adjusting your search or filters' 
                : 'Add your first contact to get started!'}
            </p>
            <button
              onClick={() => setShowAddModal(true)}
              className="inline-flex items-center space-x-2 px-6 py-2 bg-[#1F11ED] text-white rounded-lg hover:bg-[#1709C7]"
            >
              <Plus className="w-4 h-4" />
              <span>Add Contact</span>
            </button>
          </div>
        ) : (
          filteredContacts.map((contact, index) => (
            <motion.div
              key={contact.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
              className="bg-white rounded-xl shadow-sm border border-gray-200 p-4"
            >
              <div className="flex items-start space-x-4">
                {/* Avatar */}
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-full flex items-center justify-center text-white font-bold flex-shrink-0">
                  {(contact.first_name?.[0] || '') + (contact.last_name?.[0] || '')}
                </div>

                {/* Contact Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-semibold text-gray-900">
                        {contact.first_name} {contact.last_name}
                      </h3>
                      {contact.company_name && (
                        <p className="text-sm text-gray-600 flex items-center space-x-1">
                          <Building className="w-3 h-3" />
                          <span>{contact.company_name}</span>
                        </p>
                      )}
                    </div>
                    <button
                      onClick={() => toggleFavorite(contact)}
                      className="p-1"
                    >
                      <Star className={`w-5 h-5 ${contact.is_favorite ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}`} />
                    </button>
                  </div>

                  <div className="space-y-1 text-sm mb-3">
                    <a href={`mailto:${contact.email}`} className="flex items-center space-x-2 text-gray-600 hover:text-[#1F11ED]">
                      <Mail className="w-4 h-4" />
                      <span className="truncate">{contact.email}</span>
                    </a>
                    {contact.phone && (
                      <a href={`tel:${contact.phone}`} className="flex items-center space-x-2 text-gray-600 hover:text-[#1F11ED]">
                        <Phone className="w-4 h-4" />
                        <span>{contact.phone}</span>
                      </a>
                    )}
                  </div>

                  {/* Stats */}
                  <div className="flex items-center space-x-4 text-xs text-gray-500">
                    <span className="flex items-center space-x-1">
                      <FileText className="w-3 h-3" />
                      <span>{contact.invoice_count || 0} invoices</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <TrendingUp className="w-3 h-3" />
                      <span>£{(contact.total_value || 0).toLocaleString()}</span>
                    </span>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center space-x-2 mt-3">
                    <button
                      onClick={() => navigate(`/portal/send-invoice?contactId=${contact.id}`)}
                      className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-[#1F11ED] text-white rounded-lg hover:bg-[#1709C7] text-sm font-medium"
                    >
                      <Send className="w-4 h-4" />
                      <span>Send Invoice</span>
                    </button>
                    <button
                      onClick={() => setEditingContact(contact)}
                      className="flex-1 flex items-center justify-center space-x-2 px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm"
                    >
                      <Edit className="w-4 h-4" />
                      <span>Edit</span>
                    </button>
                    <button
                      onClick={() => {
                        if (window.confirm('Delete this contact?')) {
                          handleDeleteContact(contact.id);
                        }
                      }}
                      className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>

      {/* Modals */}
      {showAddModal && (
        <AddContactModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddContact}
        />
      )}

      {editingContact && (
        <EditContactModal
          contact={editingContact}
          onClose={() => setEditingContact(null)}
          onSave={(data) => handleEditContact(editingContact.id, data)}
        />
      )}
    </div>
  );
};

export default MobileContacts;

